// imports
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarIcon from '@mui/icons-material/Star';
import { Box, CircularProgress, ClickAwayListener, IconButton, MenuItem, Paper, Popper } from '@mui/material';
import React, { FC } from 'react';

// components
import { Alert } from 'components/common/Alert';

// constants, helpers, graphql, interfaces
import { CardMenuProps, Nullable } from 'interfaces';
import { DELETE_TEXT, HTTP_STATUS, MARK_AS_DEFAULT_TEXT } from 'constants/index';
import { useRemovePaymentMethodMutation, useSetDefaultPaymentMethodMutation } from 'generated/graphql';

export const CardMenu: FC<CardMenuProps> = ({ handleSuccess, cardId, isDefault }) => {
  const [anchorEl, setAnchorEl] = React.useState<Nullable<HTMLElement>>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [removePaymentMethod, { loading }] = useRemovePaymentMethodMutation({
    onCompleted: (data) => {
      const { removePaymentMethod } = data || {};
      const { response } = removePaymentMethod || {};
      const { status, message } = response || {};

      if (status === HTTP_STATUS.SUCCESS) {
        Alert.success(message ?? '');
        handleSuccess();
      } else {
        Alert.error(message ?? '');
      }
    },
    onError: (error) => {
      Alert.error(error.message || '');
    },
  });

  const [setDefaultCardMethod, { loading: cardLoading }] = useSetDefaultPaymentMethodMutation({
    onCompleted: (data) => {
      const { setDefaultPaymentMethod } = data || {};
      const { response } = setDefaultPaymentMethod || {};
      const { status, message } = response || {};

      if (status === HTTP_STATUS.SUCCESS) {
        Alert.success(message ?? '');
        handleSuccess();
      } else {
        Alert.error(message ?? '');
      }
    },
    onError: (error) => {
      Alert.error(error.message || '');
    },
  });
  const handleRemoveCard = () => {
    handleClose();
    removePaymentMethod({
      variables: {
        removePaymentMethodInput: {
          paymentMethodId: cardId,
        },
      },
    });
  };

  const handleDefaultCard = () => {
    handleClose();
    setDefaultCardMethod({
      variables: {
        setDefaultPaymentMethodInput: {
          paymentMethodId: cardId,
        },
      },
    });
  };

  return (
    <Box>
      <IconButton onClick={handleClick} disabled={loading || cardLoading}>
        {loading || cardLoading ? <CircularProgress size={20} /> : <MoreVertIcon />}
      </IconButton>

      <Popper open={open} anchorEl={anchorEl} placement="bottom-end" style={{ zIndex: 1300 }}>
        <ClickAwayListener onClickAway={handleClose}>
          <Paper>
            {loading ? (
              <Box p={2} display="flex" justifyContent="center">
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                {!isDefault && (
                  <MenuItem onClick={handleDefaultCard}>
                    <StarIcon fontSize="inherit" color="primary" sx={{ mr: 1 }} />
                    {MARK_AS_DEFAULT_TEXT}
                  </MenuItem>
                )}
                <MenuItem onClick={handleRemoveCard}>
                  <DeleteIcon fontSize="inherit" sx={{ mr: 1 }} color="error" />
                  {DELETE_TEXT}
                </MenuItem>
              </>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
